import TextBox from "../../../shared/textBox";
import {
  alwaysDisabledFields,
  conditionalDisabledFieldsEdit,
} from "../../../utils/fieldConstants";

import { useEffect, useState } from "react";
import SelectBox from "../../../shared/selectBox";
import { formOptions } from "../../../utils/Data";
import RadioInput from "../../../shared/radioInput";

interface CloseOutFieldsProps {
  formData: {
    [key: string]: string | number | Date | FileList | null;
  };
  handleChange: (value: any, key: string) => void;
  closeoutErrors: { [key: string]: string | null };
  closeoutInputErrors: { [key: string]: string | null };
  closeoutOnFocus: (key: string) => void; // Add focus handler
  closeoutOnBlur: (key: string) => void;
  mode: any;
  localProjectData: {
    preferred_vendor: any;
    project_core_deliverables: any;
    close_out_additional_phase: any;
    project_lead_name: any;
    project_lead_email: any;
    business_stack_req_group: any;
    purchase_order_number: any;
    consulting_type: any;
    estimated_project_start_date: any;
    estimated_project_end_date: any;
    consulting_company: any;
    final_total_cost: any;
    rate_value_received: any;
    core_deliverables: any;
  };
}
const CloseOutFields: React.FC<CloseOutFieldsProps> = ({
  formData,
  mode,
  closeoutErrors,
  handleChange,
  closeoutOnFocus,
  closeoutOnBlur,
  localProjectData,
  closeoutInputErrors,
}) => {
  const isFieldDisabled = (fieldName: string, mode: string): boolean => {
    // Always disable fields listed in alwaysDisabledFields
    if (alwaysDisabledFields.includes(fieldName)) {
      return true;
    }

    // In edit mode, disable fields listed in conditionalDisabledFieldsEdit
    if (mode === "edit" && conditionalDisabledFieldsEdit.includes(fieldName)) {
      return true;
    }

    return false; // Fields are enabled by default if they are not in the above lists
  };
  var defaultCoreDeliverables = localProjectData?.core_deliverables || "";
  var defaultConsultingCompany = localProjectData.preferred_vendor || "";

  var closeout_coreDeliverables = isFieldDisabled(
    "project_core_deliverables",
    mode
  )
    ? localProjectData?.project_core_deliverables || defaultCoreDeliverables
    : formData.coreDeliverables !== undefined
    ? formData.coreDeliverables
    : "";

  var closeout_consultingCompany = isFieldDisabled("consulting_company", mode)
    ? localProjectData?.consulting_company || defaultConsultingCompany
    : formData.consultingCompany !== undefined
    ? formData.consultingCompany
    : "";

  const [localServiceCost, setLocalServiceCost] = useState(
    formData.finalCostUSD || ""
  );
  // Handle input change for service cost
  const handleServiceCostChange = (value: any) => {
    // Remove any non-digit characters
    let numericValue = value.replace(/\D/g, "");
    // Remove leading zeros
    numericValue = numericValue.replace(/^0+/, "");

    // Only update the parent state if the value changes
    if (numericValue !== formData.finalCostUSD) {
      handleChange(numericValue, "finalCost");
    }
  };

  useEffect(() => {
    const formattedValue = formData.final_total_cost
      ? formData.final_total_cost
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : "";
    setLocalServiceCost(formattedValue);
  }, [formData.final_total_cost]);
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <div className="form-container">
          <TextBox
            label="What consulting company did you use?"
            value={closeout_consultingCompany}
            onChange={(value) => handleChange(value, "consultingCompany")}
            error={
              closeoutErrors.consultingCompany
                ? closeoutErrors.consultingCompany
                : closeoutInputErrors.consultingCompany
                ? closeoutInputErrors.consultingCompany
                : null
            }
            multiline={true}
            maxLength={100}
            required={true}
            rows={2}
            onFocus={() => closeoutOnFocus("consultingCompany")}
            onBlur={() => closeoutOnBlur("consultingCompany")}
            disabled={isFieldDisabled("consulting_company", mode)}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />
          <TextBox
            label="What was the final total cost (USD) for the engagement?"
            value={localProjectData.final_total_cost || localServiceCost}
            onChange={(value) => handleServiceCostChange(value)}
            error={closeoutErrors.finalCost ? closeoutErrors.finalCost : null}
            multiline={false}
            maxLength={30}
            required={true}
            rows={1}
            isCost={true}
            disabled={isFieldDisabled("final_total_cost", mode)}
            onFocus={() => closeoutOnFocus("finalCost")}
            onBlur={() => closeoutOnBlur("finalCost")}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />
          <TextBox
            label="What were the core deliverables of the project?"
            value={closeout_coreDeliverables}
            onChange={
              (value) => handleChange(value, "coreDeliverables") // Update the field with the new value
            }
            error={
              closeoutErrors.coreDeliverables
                ? closeoutErrors.coreDeliverables
                : closeoutInputErrors.coreDeliverables
                ? closeoutInputErrors.coreDeliverables
                : null
            }
            multiline={true}
            maxLength={5000}
            required={true}
            rows={2}
            disabled={isFieldDisabled("project_core_deliverables", mode)}
            onFocus={() => closeoutOnFocus("coreDeliverables")}
            onBlur={() => closeoutOnBlur("coreDeliverables")}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />
          <SelectBox
            label="Please rate the value received ?"
            value={
              localProjectData.rate_value_received || formData.rating || ""
            }
            onChange={(value) => handleChange(value, "rating")}
            onFocus={() => closeoutOnFocus("rating")}
            onBlur={() => closeoutOnBlur("rating")}
            disabled={isFieldDisabled("rate_value_received", mode)}
            required={true}
            options={formOptions.rating}
            error={closeoutErrors.rating ? closeoutErrors.rating : null}
          />
          <TextBox
            label="What is the purchase order number?"
            value={
              localProjectData.purchase_order_number ||
              formData.purchaseOrderNumber
            }
            onChange={(value) => handleChange(value, "purchaseOrderNumber")}
            multiline={false}
            maxLength={30}
            required={false}
            rows={2}
            disabled={isFieldDisabled("purchase_order_number", mode)}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />
          <RadioInput
            label={" Do you envision any follow-up work or additional phases?"}
            value={
              localProjectData.close_out_additional_phase ||
              formData.phasesTwo ||
              ""
            }
            disabled={isFieldDisabled("close_out_additional_phase", mode)}
            onchange={(value) => handleChange(value, "phasesTwo")}
          />
        </div>
      </div>
    </>
  );
};
export default CloseOutFields;
